import React, { useEffect, useState } from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { PlusCircle, X, Search, Edit2, Trash2, Eye } from 'lucide-react';

const ProgramsAdminPage = () => {
  const [categories, setCategories] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);
  const [form, setForm] = useState({
    title: '',
    category: '',
    impact: '',
    description: '',
    activities: '', // comma separated
    icon: 'ScaleIcon',
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  // Programs list state
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [animateModal, setAnimateModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  // Detail modal
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [detailProgram, setDetailProgram] = useState(null);

  useEffect(() => {
    const fallback = ['Advocacy', 'Education', 'Infrastructure', 'Community', 'Research', 'Youth'];
    const load = async () => {
      try {
        const res = await fetch('https://aaapd.org.et/gallery_backend/api/getProgramCategories.php');
        if (!res.ok) throw new Error('bad');
        const data = await res.json();
        const cats = (Array.isArray(data) ? data.map((c) => (typeof c === 'string' ? c : c.name)).filter(Boolean) : [])
          .filter((v, i, a) => a.indexOf(v) === i);
        setCategories(cats.length ? cats : fallback);
        if (!form.category && (cats.length ? cats : fallback).length) {
          setForm((f) => ({ ...f, category: (cats.length ? cats : fallback)[0] }));
        }
      } catch {
        setCategories(fallback);
        if (!form.category && fallback.length) {
          setForm((f) => ({ ...f, category: fallback[0] }));
        }
      } finally {
        setLoadingCats(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Modal and actions (component scope)
  const openAddModal = () => {
    setIsEditing(false);
    setEditId(null);
    setForm({ title: '', category: categories[0] || '', impact: '', description: '', activities: '', icon: 'ScaleIcon' });
    setIsModalOpen(true);
    setTimeout(() => setAnimateModal(true), 10);
  };

  const openEditModal = (p) => {
    setIsEditing(true);
    setEditId(p.id);
    setForm({
      title: p.title || '',
      category: p.category || (categories[0] || ''),
      impact: p.impact || '',
      description: p.description || '',
      activities: typeof p.activities === 'string' ? p.activities : Array.isArray(p.activities) ? p.activities.join(', ') : '',
      icon: p.icon || 'ScaleIcon',
    });
    setIsModalOpen(true);
    setTimeout(() => setAnimateModal(true), 10);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this program?')) return;
    try {
      const res = await fetch(`https://aaapd.org.et/gallery_backend/api/deleteProgram.php?id=${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to delete');
      }
      setStatus({ type: 'success', message: 'Program deleted successfully.' });
      fetchPrograms();
    } catch (err) {
      setStatus({ type: 'error', message: err.message || 'Failed to delete program.' });
    }
  };

  const openDetail = (p) => {
    setDetailProgram(p);
    setIsDetailOpen(true);
  };
  const closeDetail = () => {
    setIsDetailOpen(false);
    setDetailProgram(null);
  };

  // Load programs from backend
  const fetchPrograms = async () => {
    setLoading(true);
    try {
      const res = await fetch('https://aaapd.org.et/gallery_backend/api/getPrograms.php');
      if (!res.ok) throw new Error('Failed to load programs');
      const data = await res.json();
      setPrograms(Array.isArray(data) ? data : []);
    } catch (e) {
      setPrograms([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrograms();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus(null);
    try {
      const payload = {
        title: form.title,
        category: form.category,
        impact: form.impact,
        description: form.description,
        // split comma separated activities into array on the backend if needed
        activities: form.activities,
        icon: form.icon,
      };

      let url = 'https://aaapd.org.et/gallery_backend/api/addProgram.php';
      let method = 'POST';
      if (isEditing && editId) {
        url = 'https://aaapd.org.et/gallery_backend/api/editProgram.php';
        // include id for edit
        payload.id = editId;
      }

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to save');
      setStatus({ type: 'success', message: isEditing ? 'Program updated successfully.' : 'Program created successfully.' });
      setForm({ title: '', category: categories[0] || '', impact: '', description: '', activities: '', icon: 'ScaleIcon' });
      setIsEditing(false);
      setEditId(null);
      // Close modal and refresh list
      setAnimateModal(false);
      setTimeout(() => setIsModalOpen(false), 200);
      fetchPrograms();
    } catch (err) {
      setStatus({ type: 'error', message: err.message || 'Failed to create program.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Programs Management</h1>
            <p className="text-gray-600 mt-1">Manage and organize your programs</p>
          </div>
          <div className="flex gap-3 mt-12 sm:mt-20">
            <button
              onClick={openAddModal}
              className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2.5 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
            >
              <PlusCircle className="w-5 h-5" />
              <span>Add Program</span>
            </button>
          </div>
        </header>

        {/* Message */}
        {status && (
          <div className={`mb-6 p-4 rounded-lg ${status.type === 'error' ? 'bg-red-100 border-l-4 border-red-500 text-red-700' : 'bg-green-100 border-l-4 border-green-500 text-green-700'}`}>
            {status.message}
          </div>
        )}

        {/* Search */}
        <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
          <div className="relative max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search programs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
            />
          </div>
        </div>

        {/* Programs Table */}
        <div className="bg-white shadow overflow-hidden rounded-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Impact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-sm text-gray-500">Loading programs...</td>
                  </tr>
                ) : programs.filter(p => {
                  const term = searchTerm.toLowerCase();
                  return !term || (
                    (p.title||'').toLowerCase().includes(term) ||
                    (p.category||'').toLowerCase().includes(term) ||
                    (p.description||'').toLowerCase().includes(term) ||
                    (p.impact||'').toLowerCase().includes(term)
                  );
                }).length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-sm text-gray-500">No programs found. Click "Add Program" to create one.</td>
                  </tr>
                ) : (
                  programs
                    .filter(p => {
                      const term = searchTerm.toLowerCase();
                      return !term || (
                        (p.title||'').toLowerCase().includes(term) ||
                        (p.category||'').toLowerCase().includes(term) ||
                        (p.description||'').toLowerCase().includes(term) ||
                        (p.impact||'').toLowerCase().includes(term)
                      );
                    })
                    .map((p) => (
                    <tr key={p.id || `${p.title}-${p.category}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">{p.title}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{p.category}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{p.impact || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{p.created_at ? new Date(p.created_at).toLocaleDateString() : '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => openDetail(p)}
                            className="text-teal-600 hover:text-teal-900 p-1.5 rounded-md hover:bg-teal-50 transition-colors"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => openEditModal(p)}
                            className="text-blue-600 hover:text-blue-900 p-1.5 rounded-md hover:bg-blue-50 transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(p.id)}
                            className="text-red-600 hover:text-red-900 p-1.5 rounded-md hover:bg-red-50 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add Program Modal */}
      {isModalOpen && (
        <div
          className={`fixed inset-0 flex items-center justify-center z-50 transition-opacity duration-300 ${animateModal ? 'opacity-100' : 'opacity-0'}`}
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
        >
          <div className={`bg-white rounded-xl shadow-2xl w-full max-w-xl mx-4 transform transition-all duration-300 ease-in-out ${animateModal ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}>
            <div className="px-5 py-3 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                {isEditing ? (
                  <>
                    <Edit2 className="w-4 h-4 text-teal-600" /> Edit Program
                  </>
                ) : (
                  <>
                    <PlusCircle className="w-4 h-4 text-teal-600" /> Add Program
                  </>
                )}
              </h3>
              <button
                onClick={() => { setAnimateModal(false); setTimeout(() => setIsModalOpen(false), 200); }}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    name="category"
                    value={form.category}
                    onChange={handleChange}
                    disabled={loadingCats}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100"
                  >
                    {(categories.length ? categories : ['Advocacy']).map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Impact (short)</label>
                  <input
                    type="text"
                    name="impact"
                    value={form.impact}
                    onChange={handleChange}
                    placeholder="e.g., 600+ trained"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  name="description"
                  rows={4}
                  value={form.description}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Activities (comma-separated)</label>
                <input
                  type="text"
                  name="activities"
                  value={form.activities}
                  onChange={handleChange}
                  placeholder="Training, Workshops, Outreach"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setAnimateModal(false); setTimeout(() => setIsModalOpen(false), 200); }}
                  className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center px-4 py-2 rounded-md font-semibold text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-60"
                >
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  {submitting ? 'Saving…' : (isEditing ? 'Save Changes' : 'Create Program')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {isDetailOpen && detailProgram && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl mx-4">
            <div className="px-5 py-3 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Eye className="w-4 h-4 text-teal-600" /> Program Details
              </h3>
              <button onClick={closeDetail} className="text-gray-400 hover:text-gray-500">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-5 space-y-3">
              <div>
                <div className="text-sm text-gray-500">Title</div>
                <div className="text-gray-900 font-medium">{detailProgram.title}</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-500">Category</div>
                  <div className="text-gray-900">{detailProgram.category}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Impact</div>
                  <div className="text-gray-900">{detailProgram.impact || '-'}</div>
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Description</div>
                <div className="text-gray-900">{detailProgram.description || '-'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Activities</div>
                <div className="text-gray-900 whitespace-pre-wrap">
                  {Array.isArray(detailProgram.activities)
                    ? detailProgram.activities.join(', ')
                    : (detailProgram.activities || '-')}
                </div>
              </div>
              <div className="pt-3 flex justify-end">
                <button onClick={closeDetail} className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgramsAdminPage;
